import {Box, CircularProgress, Container, Skeleton, Typography} from '@mui/material';
import {useQueryClient} from '@tanstack/react-query';
import type * as React from 'react';
import {useEffect, useState} from 'react';
import EmployeeCapacityMonthSelection from '@/components/Capacity/EmployeeCapacityMonthSelection';
import EmployeeCapacityTable from '@/components/Capacity/EmployeeCapacityTable';
import MyCapacityDefaultBuilder from '@/components/Capacity/MyCapacityDefaultBuilder';
import {useEmployeeProvider} from '@/context/AllEmployeesProvider';
import getCurrentCapacityYearMonth from '@/functions/getCurrentCapacityYearMonth';
import {useGetActuals} from '@/hooks/capacity/useGetActuals';
import {useGetCapacityCombined} from '@/hooks/capacity/useGetCapacityCombined';
import {useGetCapacityDefaults} from '@/hooks/capacity/useGetCapacityDefaults';
import {useGetCapacityLabels} from '@/hooks/capacity/useGetCapacityLabels';
import {useGetAllTeams} from '@/hooks/useGetAllTeams';
import type {CapacityConfig} from '@/types/CapacityConfig';
import type {Employee} from '@/types/Employee';

type Props = {
    self : Employee;
    capacityConfig : CapacityConfig;
};

const MyCapacityDashboard = ({self, capacityConfig} : Props) : React.ReactElement => {
    const queryClient = useQueryClient();
    const {employees} = useEmployeeProvider();
    const defaultCapacityYearMonth = getCurrentCapacityYearMonth(capacityConfig);
    const [fiscalMonth, setFiscalMonth] = useState<string | null>(
        getCurrentCapacityYearMonth(capacityConfig) ?? null
    );

    // queries
    const getTeamsQuery = useGetAllTeams();
    const getCapacityLabelsQuery = useGetCapacityLabels();
    const {data: capacityCombined} = useGetCapacityCombined({
        capacityYearMonth: fiscalMonth ?? '',
        employees: [self],
    });
    const getCapacityDefaultsQuery = useGetCapacityDefaults({
        employees: [self],
    });

    const {data: actuals} = useGetActuals({
        fiscalMonth: fiscalMonth ?? '',
        months: 8,
        capacityConfig,
        employees: [self],
    });

    useEffect(() => {
        const forceCombinedFetch = async () => {
            await queryClient.invalidateQueries({queryKey: ['capacityCombined']});
        };

        void forceCombinedFetch();
    }, [fiscalMonth]);

    if (employees === null) {
        return <Box>
            One
            <CircularProgress/>
        </Box>;
    }

    return <Container
        maxWidth={false}
        sx={{
            height: '100vh',
            display: 'flex',
            flexDirection: 'column',
        }}
    >
        {fiscalMonth && <MyCapacityDefaultBuilder
            employees={employees}
            capacityYearMonth={fiscalMonth}
            employeeId={self.id}
            capacityDefaults={getCapacityDefaultsQuery.data}
        />}

        {capacityCombined && getCapacityLabelsQuery.data && getTeamsQuery.data && <Box
            sx={{
                m: 3,
            }}
        >
            <Typography sx={{fontSize: '24px'}}>My Calendar</Typography>
            <Typography sx={{fontSize: '14px', mb: 3}}>
                Add other non-recurring internal time commitments and determine your capacity for the year.
            </Typography>

            {(fiscalMonth && defaultCapacityYearMonth)
                ? <Box
                    sx={{
                        mb: 2,
                    }}
                >
                    <EmployeeCapacityMonthSelection
                        fiscalMonth={fiscalMonth}
                        setFiscalMonth={setFiscalMonth}
                        capacityConfig={capacityConfig}
                        defaultCapacityYearMonth={defaultCapacityYearMonth}
                    />
                </Box>
                : <Skeleton variant="rectangular" width="100%" height={300}/>
            }

            {fiscalMonth && defaultCapacityYearMonth && <EmployeeCapacityTable
                key={`capacity-${self.id}`}
                employee={self}
                teams={getTeamsQuery.data}
                actual={actuals ? actuals[0] : undefined}
                fiscalMonth={fiscalMonth}
                capacityCombined={capacityCombined}
                labels={getCapacityLabelsQuery.data}
                defaultCapacityYearMonth={defaultCapacityYearMonth}
                capacityConfig={capacityConfig}
                capacityDefaults={getCapacityDefaultsQuery.data}
            />}
        </Box>}
    </Container>;
};

export default MyCapacityDashboard;
